/**
 * Pagination Component
 * 
 * Reusable pagination control displaying page navigation buttons and
 * item count information. Supports previous/next navigation with a
 * windowed page range (max 5 visible pages + ellipsis).
 */

import React, { memo, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

const Pagination = memo(({
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
  showInfo = true
}) => {
  const { t } = useTranslation();
  const totalPages = useMemo(() => Math.ceil(totalItems / itemsPerPage), [totalItems, itemsPerPage]);
  const startIndex = useMemo(() => (currentPage - 1) * itemsPerPage, [currentPage, itemsPerPage]);
  const endIndex = useMemo(() => Math.min(startIndex + itemsPerPage, totalItems), [startIndex, itemsPerPage, totalItems]);

  // Build a windowed list of page numbers with ellipsis markers
  const pageItems = useMemo(() => {
    if (totalPages <= 1) { return []; }
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const items = [];
    const delta = 2; // pages on each side of current

    const rangeStart = Math.max(2, currentPage - delta);
    const rangeEnd = Math.min(totalPages - 1, currentPage + delta);

    items.push(1);

    if (rangeStart > 2) { items.push('...'); }

    for (let i = rangeStart; i <= rangeEnd; i++) {
      items.push(i);
    }

    if (rangeEnd < totalPages - 1) { items.push('...'); }

    items.push(totalPages);

    return items;
  }, [totalPages, currentPage]);

  const handleNav = useCallback((page) => {
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  }, [currentPage, totalPages, onPageChange]);

  if (totalPages <= 1) {
    return null;
  }

  return (
    <div
      className="pagination"
      role="navigation"
      aria-label={t('pagination', { ns: 'common', defaultValue: 'Pagination' })}
    >
      <div className="pagination__controls">
        <button
          type="button"
          className="pagination__nav"
          onClick={() => handleNav(currentPage - 1)}
          disabled={currentPage === 1}
        >
          {t('previous', { ns: 'common' })}
        </button>

        <div className="pagination__pages">
          {pageItems.map((page, idx) =>
            page === '...' ? (
              <span
                key={`ellipsis-${idx}`}
                className="pagination__ellipsis"
                style={{
                  padding: '0 6px',
                  color: 'var(--text-muted)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  userSelect: 'none',
                }}
              >
                …
              </span>
            ) : (
              <button
                key={page}
                type="button"
                className={`pagination__page ${currentPage === page ? 'pagination__page--active' : ''}`}
                onClick={() => handleNav(page)}
                aria-current={currentPage === page ? 'page' : undefined}
              >
                {page}
              </button>
            )
          )}
        </div>

        <button
          type="button"
          className="pagination__nav"
          onClick={() => handleNav(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          {t('next', { ns: 'common' })}
        </button>
      </div>

      {showInfo && (
        <div className="pagination__info">
          {t('showingItems', {
            ns: 'common',
            defaultValue: 'Showing {{start}}–{{end}} of {{total}}',
            start: startIndex + 1,
            end: endIndex,
            total: totalItems,
          })}
        </div>
      )}
    </div>
  );
});

Pagination.displayName = 'Pagination';

export default Pagination;
